import apiEndpoints from 'api/endpoints';

import { baseAPI } from './baseApi';

// baseAPI's reducer and middleware are already registered in the store, so
// injecting here needs no change to initStore. 'Themes' is declared in baseAPI's
// tagTypes — without that declaration RTK Query ignores the tags silently and
// the list never refreshes after a mutation.

// The single theme's logo_url is a signed S3 URL that expires after 5
// minutes, so that one query can't cache. The list never renders logoUrl
// (cards use config colors, not the image), so it keeps RTK Query's normal
// cache — switching between the Workflows and Themes tabs reuses it instead
// of refetching every time; any mutation still invalidates it via tags.
const SINGLE_THEME_CACHE_SECONDS = 0;

export const themesApi = baseAPI.injectEndpoints({
    endpoints: (builder) => ({
        fetchAllThemes: builder.query({
            query: () => ({
                url: apiEndpoints.themesUrl,
                method: 'GET',
            }),
            providesTags: ['Themes'],
        }),

        // Returns the same theme object the list returns, wrapped as { theme }.
        fetchSingleTheme: builder.query({
            query: (nanoid) => ({
                url: `${apiEndpoints.themesUrl}/${nanoid}`,
                method: 'GET',
            }),
            keepUnusedDataFor: SINGLE_THEME_CACHE_SECONDS,
            providesTags: (result, error, nanoid) => [{ type: 'Themes', id: nanoid }],
        }),

        createTheme: builder.mutation({
            query: (body) => ({
                url: apiEndpoints.themesUrl,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Themes'],
        }),

        duplicateTheme: builder.mutation({
            query: ({ nanoid, body }) => ({
                url: `${apiEndpoints.themesUrl}/${nanoid}/duplicate`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Themes'],
        }),

        updateTheme: builder.mutation({
            query: ({ nanoid, body }) => ({
                url: `${apiEndpoints.themesUrl}/${nanoid}`,
                method: 'PUT',
                body,
            }),
            // A failed PUT must not invalidate the single-theme query — that
            // triggers a refetch with no cached data yet, which briefly sets
            // isLoading back to true and unmounts the whole editor mid-edit.
            invalidatesTags: (result, error, { nanoid }) =>
                error ? [] : ['Themes', { type: 'Themes', id: nanoid }],
        }),

        setDefaultTheme: builder.mutation({
            query: (nanoid) => ({
                url: `${apiEndpoints.themesUrl}/${nanoid}/default`,
                method: 'PUT',
            }),
            invalidatesTags: ['Themes'],
        }),

        // Soft delete. Returns 409 with the blocking workflows if any live
        // workflow still uses the theme.
        deleteTheme: builder.mutation({
            query: (nanoid) => ({
                url: `${apiEndpoints.themesUrl}/${nanoid}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, nanoid) => ['Themes', { type: 'Themes', id: nanoid }],
        }),
    }),
});

export const {
    useFetchAllThemesQuery,
    useLazyFetchAllThemesQuery,
    useFetchSingleThemeQuery,
    useLazyFetchSingleThemeQuery,
    useCreateThemeMutation,
    useDuplicateThemeMutation,
    useSetDefaultThemeMutation,
    useUpdateThemeMutation,
    useDeleteThemeMutation,
} = themesApi;
